import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { AuthUser, LoginResponse, RegisterResponse, RegisterUser } from "../models";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { environment } from "../../../environments/environment";
import { jwtDecode, JwtPayload } from "jwt-decode";

@Injectable({
    providedIn: 'root'
})
export class AuthService{
    private httpClient = inject(HttpClient)
    private router = inject(Router);
    
    private readonly baseApiUrl = `${environment.authServiceApiBaseUrl}/api/auth`;

    //Signals-ready approach, we can also use BehaviourSubject for the same thing
    private readonly _initialized = signal(false);
    private readonly _token = signal<string | null>(null);

    readonly token = this._token.asReadonly();

    private readonly TOKEN_KEY = environment.localStorageKeys.authToken;

    readonly isInitialized = this._initialized.asReadonly();
    readonly isLoggedIn = computed(() => !!this._token());

    initAuth() {
        this.restoreSession();
        this._initialized.set(true);
    }

    constructor(){
        this.restoreSession();
    }

    login(email: string, password: string, remember: boolean): Observable<AuthUser>{
        return this.httpClient
            .post<LoginResponse>(`${this.baseApiUrl}/login`, {email, password})
            .pipe(
                tap((res) => {
                    this.setSession(res, remember);
                }),
                map((res) => res.user),
                catchError(err => {
                    console.error('Login failed', err);
                    return throwError(() => err);
                })
            );
    }

    logout() : void{
        this._token.set(null);
        localStorage.removeItem(this.TOKEN_KEY);
        sessionStorage.removeItem(this.TOKEN_KEY);
        this.router.navigateByUrl('/');
    }

    LoginFailReset() : void{
        this._token.set(null);
        localStorage.removeItem(this.TOKEN_KEY);
        sessionStorage.removeItem(this.TOKEN_KEY);
    }

    register(request: RegisterUser): Observable<RegisterResponse> {
        return this.httpClient
            .post<RegisterResponse>(`${this.baseApiUrl}/register`, request)
            .pipe(
            catchError(err => {
                console.error(err);
                throw err; // or return throwError(() => err)
            })
        );
    }

    triggerForgotPassword(email: string):Observable<boolean>{
        return this.httpClient
            .post(`${this.baseApiUrl}/forgot-password`, {email})
            .pipe(
                map(() => true),
                catchError(() => of(false))
            )
    }

    isTokenExpired(token: string): boolean {
        try {
            const decoded = jwtDecode<JwtPayload>(token);

            if (!decoded.exp) {
                return true;
            }

            const now = Math.floor(Date.now() / 1000);
            return decoded.exp < now;
        } catch {
            return true;
        }
    }

    //#region private methods

    private setSession(res : LoginResponse, remember: boolean): void{
        this._token.set(res.accessToken);
        if(remember){
            localStorage.setItem(this.TOKEN_KEY, res.accessToken);
        }else{
            sessionStorage.setItem(this.TOKEN_KEY, res.accessToken);
        }
    }

    private restoreSession(): void{
        const token = 
            localStorage.getItem(this.TOKEN_KEY) ?? 
            sessionStorage.getItem(this.TOKEN_KEY);

        if(!token){
            return;
        }

        if(this.isTokenExpired(token)){
            this.logout();
            return;
        }

        if(token){
            try{
                this._token.set(token);
                this._initialized.set(true);
            } catch{
                this.logout();
            }
        }
    }

    //#endregion
    
}